/**
 * ExMarked — Download / Export Library
 *
 * Five export formats:
 *   downloadAsMarkdown  — raw source (.md)
 *   downloadAsText      — stripped plain text (.txt)
 *   downloadAsPDF       — browser print dialog (best quality, zero deps)
 *   downloadAsRTF       — rich text format (.rtf)
 *   downloadAsDocx      — Word document (.docx) — lazy-loaded via dynamic import
 */

import { unified }     from 'unified'
import remarkParse     from 'remark-parse'
import remarkGfm       from 'remark-gfm'
import remarkRehype    from 'remark-rehype'

// ─── Shared ───────────────────────────────────────────────────────────────────

function triggerDownload(content: string | ArrayBuffer | Blob, filename: string, mime: string): void {
  const blob = content instanceof Blob
    ? content
    : new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a   = document.createElement('a')
  a.href     = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

/** Derive a base filename (no ext) from a repo file path. */
export function baseFilename(filePath: string): string {
  const name = filePath.split('/').pop() ?? filePath
  return name.replace(/\.mdx?$/i, '') || 'document'
}

// ─── Simplified mdast node interface (avoid heavy @types/mdast dep) ───────────

interface MdNode {
  type:      string
  children?: MdNode[]
  value?:    string
  depth?:    number
  ordered?:  boolean
  lang?:     string | null
  url?:      string
  alt?:      string | null
}

function parseMd(content: string): MdNode {
  return unified().use(remarkParse).use(remarkGfm).parse(content) as unknown as MdNode
}

// ─── 1. Markdown ──────────────────────────────────────────────────────────────

export function downloadAsMarkdown(content: string, filename: string): void {
  triggerDownload(content, `${filename}.md`, 'text/markdown;charset=utf-8')
}

// ─── 2. Plain Text ────────────────────────────────────────────────────────────

export function downloadAsText(content: string, filename: string): void {
  triggerDownload(stripMarkdown(content), `${filename}.txt`, 'text/plain;charset=utf-8')
}

function stripMarkdown(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*\*(.+?)\*\*\*/gs, '$1')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/\*(.+?)\*/gs, '$1')
    .replace(/~~(.+?)~~/gs, '$1')
    .replace(/```[\s\S]*?```/gm, (m) => m.replace(/```\w*\n?/, '').replace(/```/, '').trim())
    .replace(/`(.+?)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^>\s*/gm, '')
    .replace(/^[-*_]{3,}\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// ─── 3. PDF (browser print) ───────────────────────────────────────────────────

export function downloadAsPDF(): void {
  window.print()
}

// ─── 4. RTF ───────────────────────────────────────────────────────────────────

export function downloadAsRTF(content: string, filename: string): void {
  const rtf = buildRTF(parseMd(content))
  triggerDownload(rtf, `${filename}.rtf`, 'application/rtf;charset=utf-8')
}

function escRTF(text: string): string {
  return (text ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}')
    .replace(/[^\x00-\x7F]/g, (c) => `\\u${c.charCodeAt(0)}?`)
}

function rtfInline(nodes: MdNode[]): string {
  return (nodes ?? []).map((n) => {
    switch (n.type) {
      case 'text':       return escRTF(n.value ?? '')
      case 'strong':     return `{\\b ${rtfInline(n.children ?? [])}}`
      case 'emphasis':   return `{\\i ${rtfInline(n.children ?? [])}}`
      case 'inlineCode': return `{\\f1\\fs20 ${escRTF(n.value ?? '')}}`
      case 'link':       return rtfInline(n.children ?? [])
      case 'image':      return `[${escRTF(n.alt ?? 'image')}]`
      case 'break':      return '\\line '
      default:           return ''
    }
  }).join('')
}

function rtfBlock(nodes: MdNode[], indentLeft = 0): string {
  const li = indentLeft > 0 ? `\\li${indentLeft} ` : ''

  return (nodes ?? []).map((n) => {
    switch (n.type) {
      case 'heading': {
        const sizes = [48, 40, 34, 28, 24, 22]
        const sz    = sizes[Math.min((n.depth ?? 1) - 1, 5)]
        return `\\pard\\sb360\\sa120${li}\\b\\fs${sz} ${rtfInline(n.children ?? [])}\\b0\\fs24\\par\n`
      }
      case 'paragraph':
        return `\\pard\\sa160${li} ${rtfInline(n.children ?? [])}\\par\n`

      case 'code': {
        const lines = (n.value ?? '').split('\n')
        const body  = lines.map((l) => `\\pard\\f1\\fs20\\cb2${li} ${escRTF(l)}\\f0\\fs24\\par`).join('\n')
        return body + '\n'
      }
      case 'blockquote':
        return rtfBlock(n.children ?? [], indentLeft + 720)

      case 'list':
        return (n.children ?? []).map((item, i) => {
          const text = rtfInline(
            (item.children ?? []).flatMap((c) =>
              c.type === 'paragraph' ? (c.children ?? []) : []
            )
          )
          const marker = n.ordered ? `${i + 1}.` : '\\bullet'
          return `\\pard\\fi-360\\li${360 + indentLeft} ${marker}  ${text}\\par\n`
        }).join('')

      case 'thematicBreak':
        return `\\pard\\brdrb\\brdrs\\brdrw10\\brsp20 \\par\n`

      default:
        return ''
    }
  }).join('')
}

function buildRTF(tree: MdNode): string {
  const body = rtfBlock(tree.children ?? [])
  return [
    '{\\rtf1\\ansi\\ansicpg1252\\deff0\\nouicompat',
    '{\\fonttbl{\\f0\\froman\\fcharset0 Times New Roman;}{\\f1\\fmodern\\fcharset0 Courier New;}}',
    '{\\colortbl ;\\red30\\green30\\blue30;\\red22\\green27\\blue34;}',
    '\\paperw12240\\paperh15840\\margl1800\\margr1800\\margt1440\\margb1440',
    '\\widowctrl\\hyphauto\\f0\\fs24\\cf1',
    body,
    '}',
  ].join('\n')
}

// ─── 5. Word (.docx) — lazy-loaded ───────────────────────────────────────────

// ─── HAST node types (compile-time only) ─────────────────────────────────────

interface HastText    { type: 'text';    value: string }
interface HastElement { type: 'element'; tagName: string; properties: Record<string, unknown>; children: HastNode[] }
interface HastRoot    { type: 'root';    children: HastNode[] }
type HastNode = HastText | HastElement | HastRoot | { type: string }

// ─── Shared HAST → docx block builder ────────────────────────────────────────
// Accepts pre-imported docx constructors so the dynamic import is done once
// per export operation, shared between single-file and Trove exports.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildBlocks(content: string, lib: any): any[] {
  const {
    Paragraph, TextRun, HeadingLevel, BorderStyle, AlignmentType,
    Table, TableRow, TableCell, WidthType,
  } = lib

  const isEl   = (n: HastNode): n is HastElement => n.type === 'element'
  const isText = (n: HastNode): n is HastText    => n.type === 'text'

  const processor = unified().use(remarkParse).use(remarkGfm).use(remarkRehype)
  const hast = processor.runSync(processor.parse(content)) as HastRoot

  const HEADING_LEVELS = [
    HeadingLevel.HEADING_1, HeadingLevel.HEADING_2, HeadingLevel.HEADING_3,
    HeadingLevel.HEADING_4, HeadingLevel.HEADING_5, HeadingLevel.HEADING_6,
  ]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function inline(nodes: HastNode[], bold = false, italics = false, code = false, color?: string): any[] {
    return nodes.flatMap((n): any[] => {
      if (isText(n)) return [new TextRun({
        text:   n.value,
        bold,   italics,
        color,
        font:   code ? 'Courier New' : undefined,
        size:   code ? 18           : undefined,
      })]
      if (!isEl(n)) return []
      switch (n.tagName) {
        case 'strong': case 'b':  return inline(n.children, true,  italics, code, color)
        case 'em':     case 'i':  return inline(n.children, bold,  true,    code, color)
        case 'code':              return inline(n.children, bold,  italics, true, color)
        case 'a':                 return inline(n.children, bold,  italics, code, color)
        case 'br':                return [new TextRun({ text: '', break: 1 })]
        case 'img':               return [new TextRun({ text: `[${String(n.properties?.alt ?? 'image')}]`, color: '888888' })]
        default:                  return inline(n.children, bold, italics, code, color)
      }
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function cellInline(children: HastNode[], textColor?: string): any[] {
    const nonEmpty = children.filter(c => !isText(c) || (c as HastText).value.trim() !== '')
    if (nonEmpty.length === 1 && isEl(nonEmpty[0]) && nonEmpty[0].tagName === 'p') {
      return inline(nonEmpty[0].children, false, false, false, textColor)
    }
    return inline(children, false, false, false, textColor)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function blocks(nodes: HastNode[], listLevel = -1, isOrdered = false, quoteDepth = 0): any[] {
    return nodes.flatMap((n): any[] => {
      if (isText(n)) {
        return n.value.trim() ? [new Paragraph({ children: [new TextRun({ text: n.value })] })] : []
      }
      if (!isEl(n)) return []

      const quoteIndent = quoteDepth * 720
      const quoteBorder = quoteDepth > 0
        ? { left: { style: BorderStyle.THICK, size: 4, color: 'FF8C00', space: 8 } }
        : undefined

      switch (n.tagName) {
        case 'h1': case 'h2': case 'h3':
        case 'h4': case 'h5': case 'h6':
          return [new Paragraph({
            children: inline(n.children),
            heading:  HEADING_LEVELS[parseInt(n.tagName[1]) - 1],
            spacing:  { before: 360, after: 120 },
          })]

        case 'p':
          return [new Paragraph({
            children: inline(n.children),
            indent:   quoteIndent ? { left: quoteIndent } : undefined,
            border:   quoteBorder,
            spacing:  { after: 160 },
          })]

        case 'blockquote':
          return blocks(n.children, listLevel, isOrdered, quoteDepth + 1)

        case 'pre': {
          const codeEl  = n.children.find(c => isEl(c) && c.tagName === 'code') as HastElement | undefined
          const codeStr = (codeEl ?? n).children.filter(isText).map(t => (t as HastText).value).join('')
          const lines   = codeStr.replace(/\n$/, '').split('\n')
          const paras   = lines.map(line => new Paragraph({
            children: [new TextRun({ text: line, font: 'Courier New', size: 18, color: 'C9D1D9' })],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            shading:  { type: 'clear', fill: '161B22' } as any,
            spacing:  { after: 0 },
          }))
          paras.push(new Paragraph({ spacing: { after: 160 } }))
          return paras
        }

        case 'ul': return blocks(n.children, listLevel + 1, false, quoteDepth)
        case 'ol': return blocks(n.children, listLevel + 1, true,  quoteDepth)

        case 'li': {
          const nestedLists  = n.children.filter(c => isEl(c) && (c.tagName === 'ul' || c.tagName === 'ol'))
          const contentNodes = n.children.filter(c => !isEl(c) || (c.tagName !== 'ul' && c.tagName !== 'ol'))
          const bullet = new Paragraph({
            children:  cellInline(contentNodes),
            numbering: { reference: isOrdered ? 'ordered-list' : 'bullet-list', level: listLevel },
            spacing:   { after: 60 },
          })
          const nested = nestedLists.flatMap(c => blocks([c], listLevel, isOrdered, quoteDepth))
          return [bullet, ...nested]
        }

        case 'table': {
          const thead = n.children.find(c => isEl(c) && c.tagName === 'thead') as HastElement | undefined
          const tbody = n.children.find(c => isEl(c) && c.tagName === 'tbody') as HastElement | undefined
          const headerRows = (thead?.children ?? []).filter(c => isEl(c) && c.tagName === 'tr') as HastElement[]
          const bodyRows   = (tbody?.children  ?? []).filter(c => isEl(c) && c.tagName === 'tr') as HastElement[]
          const firstRow   = headerRows[0] ?? bodyRows[0]
          const colCount   = firstRow ? firstRow.children.filter(isEl).length : 1
          const colWidth   = Math.floor(9360 / colCount)

          const buildRow = (row: HastElement, isHeader: boolean) =>
            new TableRow({
              tableHeader: isHeader,
              children: row.children.filter(isEl).map(cell =>
                new TableCell({
                  width:   { size: colWidth, type: WidthType.DXA },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  shading: isHeader ? { type: 'clear', fill: '0A0A0B' } as any : undefined,
                  children: [new Paragraph({
                    children: cellInline(
                      (cell as HastElement).children,
                      isHeader ? 'FFFFFF' : undefined,
                    ),
                    spacing:  { before: 60, after: 60 },
                  })],
                })
              ),
            })

          return [
            new Table({
              columnWidths: Array(colCount).fill(colWidth) as number[],
              rows: [
                ...headerRows.map(r => buildRow(r, true)),
                ...bodyRows.map(r  => buildRow(r, false)),
              ],
            }),
            new Paragraph({ spacing: { after: 200 } }),
          ]
        }

        case 'hr':
          return [new Paragraph({
            children: [new TextRun({ text: '' })],
            border:   { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'FF4D00', space: 4 } },
            spacing:  { before: 240, after: 240 },
            alignment: AlignmentType.CENTER,
          })]

        case 'div': case 'section': case 'article':
          return blocks(n.children, listLevel, isOrdered, quoteDepth)

        default:
          return []
      }
    })
  }

  return blocks(hast.children)
}

// ─── Shared docx Document config builder ─────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeNumberingConfig(lib: any) {
  const { LevelFormat, AlignmentType } = lib
  const BULLET_CHARS = ['•', '◦', '▪', '•', '◦', '▪', '•', '◦', '▪']
  return {
    config: [
      {
        reference: 'bullet-list',
        levels: Array.from({ length: 9 }, (_, i) => ({
          level: i, format: LevelFormat.BULLET, text: BULLET_CHARS[i],
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 720 * (i + 1), hanging: 360 } } },
        })),
      },
      {
        reference: 'ordered-list',
        levels: Array.from({ length: 9 }, (_, i) => ({
          level: i, format: LevelFormat.DECIMAL, text: `%${i + 1}.`,
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 720 * (i + 1), hanging: 360 } } },
        })),
      },
    ],
  }
}

// ─── Forge document styles ────────────────────────────────────────────────────
// Applies Forge Design System V2 color palette and Arial typography to all
// docx exports.
//
// Heading color tokens:
//   H1 → forge-ember  #FF4D00  (red-orange)
//   H2 → forge-molten #FF7A00  (orange)
//   H3 → forge-gold   #FFB800  (yellow-orange)
//   H4 → forge-amber  #FFC94D  (amber)
//   H5 → forge-ash    #8A8A92  (muted)
//   H6 → forge-ash    #8A8A92  (muted)
//
// Body text is pure black (#000000) — correct for print on white paper.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeDocumentStyles(_lib: any) {
  return {
    default: {
      document: {
        run:       { font: 'Arial', size: 22, color: '000000' },
        paragraph: { spacing: { after: 160 } },
      },
      heading1: {
        run:       { font: 'Arial', bold: true, size: 60, color: 'FF4D00' },
        paragraph: { spacing: { before: 480, after: 200 } },
      },
      heading2: {
        run:       { font: 'Arial', bold: true, size: 48, color: 'FF7A00' },
        paragraph: { spacing: { before: 360, after: 160 } },
      },
      heading3: {
        // forge-gold darkened for print legibility on white — #FFB800 is near-invisible on paper
        run:       { font: 'Arial', bold: true, size: 34, color: 'CC6D00' },
        paragraph: { spacing: { before: 280, after: 120 } },
      },
      heading4: {
        // forge-amber darkened for print legibility on white — #FFC94D is near-invisible on paper
        run:       { font: 'Arial', bold: true, size: 26, color: '9B5200' },
        paragraph: { spacing: { before: 240, after: 80 } },
      },
      heading5: {
        run:       { font: 'Arial', bold: true, italics: true, size: 22, color: '8A8A92' },
        paragraph: { spacing: { before: 200, after: 80 } },
      },
      heading6: {
        run:       { font: 'Arial', italics: true, size: 22, color: '8A8A92' },
        paragraph: { spacing: { before: 160, after: 80 } },
      },
    },
  }
}

// ─── 5. Word (.docx) — single file ───────────────────────────────────────────

export async function downloadAsDocx(content: string, filename: string): Promise<void> {
  const lib = await import('docx')
  const { Document, Packer, Paragraph, PageBreak, TableOfContents } = lib

  const doc = new Document({
    features:  { updateFields: true },
    styles:    makeDocumentStyles(lib),
    numbering: makeNumberingConfig(lib),
    sections: [{
      children: [
        new TableOfContents('Table of Contents', { headingStyleRange: '1-2', hyperlink: true }),
        new Paragraph({ children: [new PageBreak()] }),
        ...buildBlocks(content, lib),
      ],
    }],
  })

  const blob = await Packer.toBlob(doc)
  triggerDownload(blob, `${filename}.docx`,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
}

// ─── 6. Markdown — ExPedition Trove (multi-file) ─────────────────────────────

export function downloadTroveAsMarkdown(
  files: Array<{ content: string; name: string }>,
  troveTitle: string,
): void {
  const body = files.map(({ content, name }, i) => {
    const separator = i > 0 ? '\n\n---\n\n' : ''
    return `${separator}# ${name}\n\n${content.trim()}`
  }).join('')

  triggerDownload(body, `${troveTitle}.md`, 'text/markdown;charset=utf-8')
}

// ─── 7. Plain Text — ExPedition Trove (multi-file) ───────────────────────────

export function downloadTroveAsText(
  files: Array<{ content: string; name: string }>,
  troveTitle: string,
): void {
  const body = files.map(({ content, name }, i) => {
    const separator = i > 0 ? '\n\n────────────────────────────────────────\n\n' : ''
    return `${separator}${name.toUpperCase()}\n\n${stripMarkdown(content)}`
  }).join('')

  triggerDownload(body, `${troveTitle}.txt`, 'text/plain;charset=utf-8')
}

// ─── 8. Word (.docx) — ExPedition Trove (multi-file) ─────────────────────────

export async function downloadTroveAsDocx(
  files: Array<{ content: string; name: string }>,
  troveTitle: string,
): Promise<void> {
  const lib = await import('docx')
  const { Document, Packer, Paragraph, TextRun, PageBreak, TableOfContents, BorderStyle } = lib

  const bodyChildren: unknown[] = []

  files.forEach(({ content, name }, i) => {
    // Page break between files (not before the first)
    if (i > 0) bodyChildren.push(new Paragraph({ children: [new PageBreak()] }))

    // Chapter label — visually prominent but NOT a Heading style, so it won't
    // pollute the master TOC. Forge-gold text with ember bottom rule.
    bodyChildren.push(new Paragraph({
      children: [new TextRun({ text: name, bold: true, size: 36, color: 'FFB800', font: 'Arial' })],
      border:   { bottom: { style: BorderStyle.THICK, size: 4, color: 'FF4D00', space: 6 } },
      spacing:  { before: 360, after: 360 },
    }))

    bodyChildren.push(...buildBlocks(content, lib))
  })

  const doc = new Document({
    features:  { updateFields: true },
    styles:    makeDocumentStyles(lib),
    numbering: makeNumberingConfig(lib),
    sections: [{
      children: [
        new TableOfContents(troveTitle || 'Table of Contents', {
          headingStyleRange: '1-2',
          hyperlink:         true,
        }),
        new Paragraph({ children: [new PageBreak()] }),
        ...(bodyChildren as any[]),
      ],
    }],
  })

  const blob = await Packer.toBlob(doc)
  triggerDownload(blob, `${troveTitle}.docx`,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
}
